import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  split,
  InMemoryCache,
  defaultDataIdFromObject,
} from '@apollo/client'
import { getMainDefinition } from '@apollo/client/utilities'
import { onError } from 'apollo-link-error'
import localforage from 'localforage'

import { WebSocketLink } from 'apollo-link-ws'
import { persistCache } from 'apollo-cache-persist'
import Hashcode from 'hashcode'

import { handleGraphQLExceptions, handleNetworkException } from 'utils/error'

import { API_URL, ENABLE_WEBSOCKETS } from 'utils/constants'
import store from 'store'
import possibleTypes from './fragmentTypes.json'

// Do not normalize this types as they are summarized by their containers (do not have an own ID)
const nonNormalizedTypes = [
  'InvestorAmount',
  'ShareReturn', // Has ID but is not identical by ID (dependent on exit amount in ExitReturn)
  'InvestorDistribution',
  'EsopBacking',
  'InterestReceipt',
  'DividendsEvent',

  // Following needed to avoid override in revisions
  'RevisionPage',
  'CustomRevisionEntityPage',
  'LiquidationPreference',
  'AntiDilution',
  'InterestRate',
  'VestingSchedule',
  'Address',
  'ConvertibleNote',
  'BonusCompensationLevel',
  'PackageCalculation',
  // 'UserStats',
  // 'ProjectStats',

  // For performance: Do not normalize most entities
  'CnEntry',
  'ShareEntry',
  'GeneralEntry',
  'LpEntry',
  'CnParticipation',
  'ShareParticipation',
  'ExitParticipation',
  'EsopParticipation',
  'GrantedParticipation',
  'DebtParticipation',
  // 'SimulationRound',
  // 'ExitRound',
  // 'ExitReturn',
  // 'ExitSummary',
  // 'ShareReturn',
  // 'InvestorReturn',
  // 'Termsheet',
  // 'CustomRevisionEntityPage',
  // 'RevisionPage',
  // 'ADEvent',
  // 'ADRange',
  // 'Address',
  // 'Business',
  // 'BuyOut',
  // 'CNRange',
  // 'CapTable',
  // 'CnConversion',
  // 'CnInvestment',
  // 'Company',
  // 'ConvertibleNote',
  // 'DividendsEvent',
  // 'DoubleRange',
  // 'EquityInvestment',
  // 'EquityRange',
  // 'Esop',
  // 'EsopBacking',
  // 'EsopCreation',
  // 'EsopGrant',
  // 'ImageUrl',
  // 'IntRange',
  // 'InterestRateRange',
  // 'InvestmentRound',
  // 'Investor',
  // 'InvestorAmount',
  // 'InvestorPool',
  // 'InvestorPreferenceAmount',
  // 'LPRange',
  // 'LiquidationPreference',
  // 'ManualShares',
  // 'PayOut',
  // 'ProjectDocument',
  // 'ProjectDocumentId',
  // 'ProjectInvitation',
  // 'ProjectMembership',
  // 'Quota',
  // 'ScenarioNode',
  // 'Share',
  // 'ShareInfo',
  // 'SharePoint',
  // 'ShareReturn',
  // 'SimulationTree',
  // 'SuperNode',
  // 'Termsheet',
  // 'UserAccount',
  // 'VestingPeriod',
]

// New way to set defaults
const defaults = {
  Query: {
    fields: {
      projectIndex: {
        read() {
          return store.get('projectIndex') || 0
        },
      },
    },
  },
}

const typePolicies = {
  ...nonNormalizedTypes.reduce((o, key) => ({ ...o, [key]: { keyFields: false } }), {}),
  ...defaults,
}

const getId = (object) => {
  // Use hashcode for in-memory-objects
  if (object.userId) return object.userId // User Accounts special ID
  if (!object.id || object.id instanceof Object) return Hashcode.hashCode().value(object) // use hashcode of all fields
  return defaultDataIdFromObject(object) // fall back to default handling
}

const cache = new InMemoryCache({
  possibleTypes,
  dataIdFromObject: (object) => {
    const revisionSuffix = object.modifiedDate || '' // to ensure differentiation between edited-objects in revision-log
    return `${object.__typename}-${getId(object)}-${revisionSuffix}`
  },
  typePolicies,
})

export const waitOnCache = persistCache({
  cache,
  storage: localforage,
  maxSize: false,
})

export const getToken = (alt = '') => {
  const token = store.get('access_token')
  return token ? `Bearer ${token}` : alt
}

const middlewareLink = new ApolloLink((operation, forward) => {
  operation.setContext({
    headers: {
      authorization: getToken(null),
    },
  })
  return forward(operation)
})

export const httpLink = new HttpLink({
  uri: `${API_URL}/graphql`,
})

const createWS = (httpLink) => {
  const wsLink = new WebSocketLink({
    uri: `${API_URL.replace('http', 'ws')}/subscriptions`,
    options: {
      reconnect: true,
      connectionParams: {
        authToken: getToken(''),
      },
    },
  })

  const links = split(
    ({ query }) => {
      const { kind, operation } = getMainDefinition(query)
      return kind === 'OperationDefinition' && operation === 'subscription'
    },
    wsLink,
    httpLink,
  )
  return links
}

export const link = ApolloLink.from([
  // compose Links together (Error checking, Authentication and URL)
  onError(({ graphQLErrors, networkError /* operation, response, */ }) => {
    if (graphQLErrors) handleGraphQLExceptions(graphQLErrors)
    if (networkError) handleNetworkException(networkError)
  }),
  middlewareLink,
  ENABLE_WEBSOCKETS ? createWS(httpLink) : httpLink,
])

export const client = new ApolloClient({
  link,
  cache,
  resolvers: {}, // TODO: ON_APOLLO_UPDATE (REMOVE)
})
